// Key hierarchy (SECURITY.md §4): user ECDH keypair, password-wrapped private
// key, clinic data key wrapped to each member's public key.
// ECDH P-256 is used because it is available in every browser we support;
// X25519 can be added behind key_algo when Android WebView coverage allows.
import { b64url, fromB64url, randomBytes, utf8, buf } from './encoding';

export const KEY_ALGO = 'ECDH-P256';

export interface WrappedBlob { iv: string; ct: string; epk?: string } // epk = ephemeral public key for ECDH wraps

export async function generateUserKeypair(): Promise<{ publicKey: string; privateKey: CryptoKey; privateJwk: JsonWebKey }> {
  const kp = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits']);
  const pub = new Uint8Array(await crypto.subtle.exportKey('raw', kp.publicKey));
  const privateJwk = await crypto.subtle.exportKey('jwk', kp.privateKey);
  return { publicKey: b64url(pub), privateKey: kp.privateKey, privateJwk };
}

export async function importPublicKey(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', buf(fromB64url(b64)), { name: 'ECDH', namedCurve: 'P-256' }, true, []);
}

export async function importPrivateJwk(jwk: JsonWebKey): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits']);
}

/** Wrap the private key JWK under the password-derived AES key. */
export async function wrapPrivateKey(wrappingKey: CryptoKey, privateJwk: JsonWebKey): Promise<WrappedBlob> {
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: buf(iv) }, wrappingKey, buf(utf8.encode(JSON.stringify(privateJwk))));
  return { iv: b64url(iv), ct: b64url(new Uint8Array(ct)) };
}

export async function unwrapPrivateKey(wrappingKey: CryptoKey, blob: WrappedBlob): Promise<CryptoKey> {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buf(fromB64url(blob.iv)) }, wrappingKey, buf(fromB64url(blob.ct)));
  return importPrivateJwk(JSON.parse(utf8.decode(new Uint8Array(pt))) as JsonWebKey);
}

async function ecdhAesKey(privateKey: CryptoKey, publicKey: CryptoKey): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  const hk = await crypto.subtle.importKey('raw', bits, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256', salt: new ArrayBuffer(0), info: buf(utf8.encode('klinixis/keywrap/v1')) },
    hk, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'],
  );
}

/** Wrap the clinic data key for a member: ephemeral ECDH with their public key. */
export async function wrapClinicKeyFor(recipientPublicKeyB64: string, clinicKeyRaw: Uint8Array): Promise<WrappedBlob> {
  const recipient = await importPublicKey(recipientPublicKeyB64);
  const eph = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveKey', 'deriveBits']);
  const aes = await ecdhAesKey(eph.privateKey, recipient);
  const iv = randomBytes(12);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: buf(iv) }, aes, buf(clinicKeyRaw));
  const epk = new Uint8Array(await crypto.subtle.exportKey('raw', eph.publicKey));
  return { iv: b64url(iv), ct: b64url(new Uint8Array(ct)), epk: b64url(epk) };
}

export async function unwrapClinicKey(myPrivateKey: CryptoKey, blob: WrappedBlob): Promise<Uint8Array> {
  if (!blob.epk) throw new Error('missing ephemeral key');
  const aes = await ecdhAesKey(myPrivateKey, await importPublicKey(blob.epk));
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: buf(fromB64url(blob.iv)) }, aes, buf(fromB64url(blob.ct)));
  return new Uint8Array(pt);
}
